import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import axios from 'axios';
import { 
  MessageCircle, ThumbsUp, ThumbsDown, Search, Trash2, Edit, 
  Archive, RotateCcw, RefreshCw, Check, X, Eye, TrendingUp, 
  Users, Video, ChevronDown, ChevronUp, Filter, Calendar
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const CommentsReactionsAdmin = ({ authToken }) => {
  const [activeTab, setActiveTab] = useState('comments');
  
  // Comments state
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsPage, setCommentsPage] = useState(1);
  const [commentsTotal, setCommentsTotal] = useState(0);
  const [commentFilter, setCommentFilter] = useState('active'); // active, archived, all
  const [searchQuery, setSearchQuery] = useState('');
  const [editingComment, setEditingComment] = useState(null);
  const [editText, setEditText] = useState('');
  
  // Reactions state
  const [reactionsStats, setReactionsStats] = useState(null);
  const [reactionsLoading, setReactionsLoading] = useState(false);
  
  // Expanded video details
  const [expandedVideo, setExpandedVideo] = useState(null);
  const [videoReactions, setVideoReactions] = useState(null);

  useEffect(() => {
    if (activeTab === 'comments') {
      fetchComments();
    } else {
      fetchReactionsStats();
    }
  }, [activeTab, commentsPage, commentFilter, authToken]);

  const getHeaders = () => {
    const token = authToken || localStorage.getItem('adminToken') || localStorage.getItem('livestreamToken');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchComments = async () => {
    setCommentsLoading(true);
    try {
      const res = await axios.get(`${API}/api/vod-admin/comments`, {
        headers: getHeaders(),
        params: {
          page: commentsPage,
          limit: 20,
          status: commentFilter
        }
      });
      setComments(res.data.comments || []);
      setCommentsTotal(res.data.total || 0);
    } catch (err) {
      console.error('Error fetching comments:', err);
    } finally {
      setCommentsLoading(false);
    }
  };

  const fetchReactionsStats = async () => {
    setReactionsLoading(true);
    try {
      const res = await axios.get(`${API}/api/vod-admin/reactions/stats`, {
        headers: getHeaders()
      });
      setReactionsStats(res.data);
    } catch (err) {
      console.error('Error fetching reactions stats:', err);
    } finally {
      setReactionsLoading(false);
    }
  };

  const fetchVideoReactions = async (videoId) => {
    try {
      const res = await axios.get(`${API}/api/vod-admin/videos/${videoId}/reactions`, {
        headers: getHeaders()
      });
      setVideoReactions(res.data);
    } catch (err) {
      console.error('Error fetching video reactions:', err);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/api/vod-admin/comments/${commentId}`, {
        headers: getHeaders()
      });
      fetchComments();
    } catch (err) {
      console.error('Error deleting comment:', err);
      alert('Failed to delete comment');
    }
  };

  const handleArchiveComment = async (commentId) => {
    try {
      await axios.put(`${API}/api/vod-admin/comments/${commentId}/archive`, {}, {
        headers: getHeaders()
      });
      fetchComments();
    } catch (err) {
      console.error('Error archiving comment:', err);
      alert('Failed to archive comment');
    }
  };

  const handleUnarchiveComment = async (commentId) => {
    try {
      await axios.put(`${API}/api/vod-admin/comments/${commentId}/unarchive`, {}, {
        headers: getHeaders()
      });
      fetchComments();
    } catch (err) {
      console.error('Error restoring comment:', err);
      alert('Failed to restore comment');
    }
  };

  const handleEditComment = async (commentId) => {
    if (!editText.trim()) return;
    
    try {
      await axios.put(`${API}/api/vod-admin/comments/${commentId}`, 
        { content: editText },
        { headers: getHeaders() }
      );
      setEditingComment(null);
      setEditText('');
      fetchComments();
    } catch (err) {
      console.error('Error updating comment:', err);
      alert('Failed to update comment');
    }
  };

  const filteredComments = comments.filter(comment => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      comment.content?.toLowerCase().includes(query) ||
      comment.customer_name?.toLowerCase().includes(query) ||
      comment.customer_email?.toLowerCase().includes(query) ||
      comment.video_title?.toLowerCase().includes(query)
    );
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header with Tabs */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <MessageCircle className="h-6 w-6 text-purple-400" />
            Comments & Reactions
          </h2>
          <p className="text-gray-400 text-sm mt-1">Manage user comments and view reaction statistics</p>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('comments')}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              activeTab === 'comments'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            <MessageCircle className="h-4 w-4" />
            Comments
          </button>
          <button
            onClick={() => setActiveTab('reactions')}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              activeTab === 'reactions'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            <ThumbsUp className="h-4 w-4" />
            Reactions
          </button>
        </div>
      </div>

      {/* Comments Tab */}
      {activeTab === 'comments' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input
                placeholder="Search comments, users, or videos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-gray-900 border-gray-700 text-white"
              />
            </div>
            
            <div className="flex gap-2">
              <select
                value={commentFilter}
                onChange={(e) => { setCommentFilter(e.target.value); setCommentsPage(1); }}
                className="px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
              >
                <option value="active">Active Comments</option>
                <option value="archived">Archived</option>
                <option value="all">All Comments</option>
              </select>
              
              <Button
                onClick={fetchComments}
                variant="outline"
                className="border-gray-700 text-gray-400"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Comments List */}
          <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
            {commentsLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin text-purple-500" />
              </div>
            ) : filteredComments.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No comments found</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-800">
                {filteredComments.map((comment) => (
                  <div key={comment.id} className="p-4 hover:bg-gray-800/50 transition-colors">
                    <div className="flex items-start gap-4">
                      {/* User Avatar */}
                      <div className="w-10 h-10 rounded-full bg-purple-600/20 flex items-center justify-center flex-shrink-0">
                        <Users className="h-5 w-5 text-purple-400" />
                      </div>
                      
                      {/* Comment Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-medium text-white">
                            {comment.customer_name || comment.customer_email?.split('@')[0] || 'Anonymous'}
                          </span>
                          {comment.is_archived && (
                            <Badge className="bg-yellow-600/20 text-yellow-400 text-xs">Archived</Badge>
                          )}
                          {comment.edited_by_admin && (
                            <Badge className="bg-blue-600/20 text-blue-400 text-xs">Edited</Badge>
                          )}
                        </div>
                        
                        <p className="text-sm text-gray-500 mb-2">
                          {comment.customer_email}
                        </p>
                        
                        {/* Video Link */}
                        {comment.video_title && (
                          <a 
                            href={`/vod/video/${comment.video_slug || comment.video_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300 mb-2"
                          >
                            <Video className="h-3 w-3" />
                            {comment.video_title}
                          </a>
                        )}
                        
                        {/* Comment Text */}
                        {editingComment === comment.id ? (
                          <div className="flex gap-2 mt-2">
                            <Input
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                              className="bg-gray-800 border-gray-700 text-white text-sm"
                              placeholder="Edit comment..."
                            />
                            <Button
                              size="sm"
                              onClick={() => handleEditComment(comment.id)}
                              className="bg-green-600 hover:bg-green-500"
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => { setEditingComment(null); setEditText(''); }}
                              className="text-gray-400"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <p className="text-gray-300 mt-2">{comment.content || comment.review_text}</p>
                        )}
                        
                        {/* Timestamp */}
                        <p className="text-xs text-gray-600 mt-2 flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(comment.created_at)}
                          {comment.video_timestamp > 0 && (
                            <span className="ml-2 text-purple-400">
                              at {Math.floor(comment.video_timestamp / 60)}:{String(Math.floor(comment.video_timestamp % 60)).padStart(2, '0')}
                            </span>
                          )}
                        </p>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-1">
                        {!editingComment && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setEditingComment(comment.id); setEditText(comment.content || comment.review_text || ''); }}
                              className="text-gray-400 hover:text-white"
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            
                            {comment.is_archived ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleUnarchiveComment(comment.id)}
                                className="text-green-400 hover:text-green-300"
                                title="Restore"
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleArchiveComment(comment.id)}
                                className="text-yellow-400 hover:text-yellow-300"
                                title="Archive"
                              >
                                <Archive className="h-4 w-4" />
                              </Button>
                            )}
                            
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteComment(comment.id)}
                              className="text-red-400 hover:text-red-300"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {commentsTotal > 20 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Showing {Math.min((commentsPage - 1) * 20 + 1, commentsTotal)} - {Math.min(commentsPage * 20, commentsTotal)} of {commentsTotal}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCommentsPage(p => Math.max(1, p - 1))}
                  disabled={commentsPage === 1}
                  className="border-gray-700"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCommentsPage(p => p + 1)}
                  disabled={commentsPage * 20 >= commentsTotal}
                  className="border-gray-700"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Reactions Tab */}
      {activeTab === 'reactions' && (
        <div className="space-y-6">
          {reactionsLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-purple-500" />
            </div>
          ) : reactionsStats ? (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-green-900/50 to-green-800/30 rounded-xl p-5 border border-green-500/30">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                      <ThumbsUp className="h-5 w-5 text-green-400" />
                    </div>
                    <span className="text-gray-400 text-sm">Total Likes</span>
                  </div>
                  <p className="text-3xl font-bold text-white">{reactionsStats.total_likes?.toLocaleString() || 0}</p>
                </div>

                <div className="bg-gradient-to-br from-red-900/50 to-red-800/30 rounded-xl p-5 border border-red-500/30">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                      <ThumbsDown className="h-5 w-5 text-red-400" />
                    </div>
                    <span className="text-gray-400 text-sm">Total Dislikes</span>
                  </div>
                  <p className="text-3xl font-bold text-white">{reactionsStats.total_dislikes?.toLocaleString() || 0}</p>
                </div>

                <div className="bg-gradient-to-br from-purple-900/50 to-purple-800/30 rounded-xl p-5 border border-purple-500/30">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                      <Video className="h-5 w-5 text-purple-400" />
                    </div>
                    <span className="text-gray-400 text-sm">Videos Rated</span>
                  </div>
                  <p className="text-3xl font-bold text-white">{reactionsStats.videos_count || 0}</p>
                </div>

                <div className="bg-gradient-to-br from-blue-900/50 to-blue-800/30 rounded-xl p-5 border border-blue-500/30">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-blue-400" />
                    </div>
                    <span className="text-gray-400 text-sm">Like Ratio</span>
                  </div>
                  <p className="text-3xl font-bold text-white">
                    {reactionsStats.total_likes + reactionsStats.total_dislikes > 0
                      ? Math.round((reactionsStats.total_likes / (reactionsStats.total_likes + reactionsStats.total_dislikes)) * 100)
                      : 0}%
                  </p>
                </div>
              </div>

              {/* Most Liked Videos */}
              <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                  <div className="p-4 border-b border-gray-800">
                    <h3 className="font-bold text-white flex items-center gap-2">
                      <ThumbsUp className="h-5 w-5 text-green-400" />
                      Most Liked Videos
                    </h3>
                  </div>
                  <div className="divide-y divide-gray-800 max-h-[400px] overflow-y-auto">
                    {reactionsStats.most_liked?.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">No likes yet</div>
                    ) : (
                      reactionsStats.most_liked?.map((video, idx) => (
                        <div 
                          key={video.id} 
                          className="p-3 hover:bg-gray-800/50 cursor-pointer flex items-center gap-3"
                          onClick={() => {
                            if (expandedVideo === video.id) {
                              setExpandedVideo(null);
                              setVideoReactions(null);
                            } else {
                              setExpandedVideo(video.id);
                              fetchVideoReactions(video.id);
                            }
                          }}
                        >
                          <span className="text-gray-500 text-sm w-5">{idx + 1}</span>
                          <div className="w-12 h-8 rounded bg-gray-800 overflow-hidden flex-shrink-0">
                            {video.thumbnail_url ? (
                              <img src={video.thumbnail_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Video className="h-4 w-4 text-gray-600" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white truncate">{video.title}</p>
                          </div>
                          <div className="flex items-center gap-3 text-sm">
                            <span className="text-green-400 flex items-center gap-1">
                              <ThumbsUp className="h-4 w-4" /> {video.like_count || 0}
                            </span>
                            <span className="text-red-400 flex items-center gap-1">
                              <ThumbsDown className="h-4 w-4" /> {video.dislike_count || 0}
                            </span>
                          </div>
                          {expandedVideo === video.id ? (
                            <ChevronUp className="h-4 w-4 text-gray-500" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-gray-500" />
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                  <div className="p-4 border-b border-gray-800">
                    <h3 className="font-bold text-white flex items-center gap-2">
                      <ThumbsDown className="h-5 w-5 text-red-400" />
                      Most Disliked Videos
                    </h3>
                  </div>
                  <div className="divide-y divide-gray-800 max-h-[400px] overflow-y-auto">
                    {reactionsStats.most_disliked?.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">No dislikes yet</div>
                    ) : (
                      reactionsStats.most_disliked?.map((video, idx) => (
                        <div 
                          key={video.id} 
                          className="p-3 hover:bg-gray-800/50 cursor-pointer flex items-center gap-3"
                          onClick={() => {
                            if (expandedVideo === video.id) {
                              setExpandedVideo(null);
                              setVideoReactions(null);
                            } else {
                              setExpandedVideo(video.id);
                              fetchVideoReactions(video.id);
                            }
                          }}
                        >
                          <span className="text-gray-500 text-sm w-5">{idx + 1}</span>
                          <div className="w-12 h-8 rounded bg-gray-800 overflow-hidden flex-shrink-0">
                            {video.thumbnail_url ? (
                              <img src={video.thumbnail_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Video className="h-4 w-4 text-gray-600" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white truncate">{video.title}</p>
                          </div>
                          <div className="flex items-center gap-3 text-sm">
                            <span className="text-green-400 flex items-center gap-1">
                              <ThumbsUp className="h-4 w-4" /> {video.like_count || 0}
                            </span>
                            <span className="text-red-400 flex items-center gap-1">
                              <ThumbsDown className="h-4 w-4" /> {video.dislike_count || 0}
                            </span>
                          </div>
                          {expandedVideo === video.id ? (
                            <ChevronUp className="h-4 w-4 text-gray-500" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-gray-500" />
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Recent Reactions */}
              <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                <div className="p-4 border-b border-gray-800">
                  <h3 className="font-bold text-white flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-purple-400" />
                    Recent Reactions
                  </h3>
                </div>
                <div className="divide-y divide-gray-800 max-h-[400px] overflow-y-auto">
                  {reactionsStats.recent_reactions?.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">No reactions yet</div>
                  ) : (
                    reactionsStats.recent_reactions?.map((reaction, idx) => (
                      <div key={idx} className="p-3 flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          reaction.reaction_type === 'like' ? 'bg-green-500/20' : 'bg-red-500/20'
                        }`}>
                          {reaction.reaction_type === 'like' ? (
                            <ThumbsUp className="h-4 w-4 text-green-400" />
                          ) : (
                            <ThumbsDown className="h-4 w-4 text-red-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white">
                            <span className="font-medium">{reaction.customer_name || reaction.customer_email?.split('@')[0] || 'User'}</span>
                            <span className="text-gray-500"> {reaction.reaction_type === 'like' ? 'liked' : 'disliked'} </span>
                            <span className="text-purple-400">{reaction.video_title || 'a video'}</span>
                          </p>
                          <p className="text-xs text-gray-600">{formatDate(reaction.created_at)}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <ThumbsUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No reaction data available</p>
            </div>
          )}
        </div>
      )}

      {/* Video Reactions Detail Modal */}
      {expandedVideo && videoReactions && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-gray-800 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-white">{videoReactions.video?.title}</h3>
                <p className="text-sm text-gray-500">Reaction Details</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setExpandedVideo(null); setVideoReactions(null); }}
                className="text-gray-400"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="p-4">
              {/* Summary */}
              <div className="flex items-center justify-center gap-8 mb-6">
                <div className="text-center">
                  <div className="flex items-center gap-2 text-green-400 text-2xl font-bold">
                    <ThumbsUp className="h-6 w-6" />
                    {videoReactions.summary?.likes || 0}
                  </div>
                  <p className="text-xs text-gray-500">Likes</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center gap-2 text-red-400 text-2xl font-bold">
                    <ThumbsDown className="h-6 w-6" />
                    {videoReactions.summary?.dislikes || 0}
                  </div>
                  <p className="text-xs text-gray-500">Dislikes</p>
                </div>
              </div>
              
              {/* Reactions List */}
              <div className="max-h-[400px] overflow-y-auto space-y-2">
                {videoReactions.reactions?.length === 0 ? (
                  <p className="text-center text-gray-500 py-4">No reactions yet</p>
                ) : (
                  videoReactions.reactions?.map((reaction, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-2 bg-gray-800/50 rounded-lg">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        reaction.reaction_type === 'like' ? 'bg-green-500/20' : 'bg-red-500/20'
                      }`}>
                        {reaction.reaction_type === 'like' ? (
                          <ThumbsUp className="h-4 w-4 text-green-400" />
                        ) : (
                          <ThumbsDown className="h-4 w-4 text-red-400" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-white">{reaction.customer_name || reaction.customer_email || 'Anonymous'}</p>
                        <p className="text-xs text-gray-500">{formatDate(reaction.created_at)}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommentsReactionsAdmin;
